/* eslint-disable react/prop-types */
/**
 * DealFormDrawer — shared create / edit drawer for a parent deal.
 *
 * Hosts a caller-owned `<Form>` wrapping the library's `WigMetadataForm`
 * (the component renders metadata-driven `Form.Item`s but does NOT own the
 * `<Form>` — see its .d.ts contract). One drawer serves both:
 *   - mode="create": empty initialValues -> dealsService.createDeal
 *   - mode="edit":   initialValues=deal  -> dealsService.updateDeal
 *
 * Assignment (swim-lane gap #3) is delivered THROUGH this same form: the
 * deals metadata marks `brokerage_id` (Lookup -> brokers_xref) and `agent`
 * (Text) as editable, so they render as ordinary fields here. There is no
 * separate assignment UI — assigning a brokerage/agent is just editing
 * those two fields and saving.
 *
 * Lookup wiring: the deals metadata keys the brokerage Lookup with
 * `distinct_values_lookup = "brokers_xref"`, so the lookupFetchers map MUST
 * be keyed `brokers_xref` (the metadata still names the legacy table even
 * though data now comes from `/brokers`). `warwick_landman` (-> `/landmen`),
 * `aois` and `deal_status` are wired too because their lookup endpoints exist.
 * `deal_status` is keyed by
 * its `distinct_values_lookup = "deal_status"`; the stored value IS the
 * status string (no separate id), so options map `{ id: v, label: v }`.
 *
 * The descriptive deal enums (`op_non_op`, `deal_category`,
 * `operator_notified`, `deal_type`) are now managed pick lists: the backend
 * inlines their active values into `/metadata` as an `options` array (from the
 * `pick_lists` table) for those `input_type='select'` fields. WigMetadataForm
 * renders that inline `options` list as a static Select automatically — the
 * metadata is passed through unaltered here, so no per-field wiring is needed.
 * (Fields the backend has NOT yet seeded arrive without `options` and still
 * fall back to a plain text input — the library's documented behaviour.)
 *
 * Errors: the backend rejects a duplicate `deal_key` with 409 and unknown
 * fields with 422 — both surfaced via an inline Alert + a message toast.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, ConfigProvider, Drawer, Form, Space, message } from "antd";

import { WigMetadataForm } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../AppContextProvider";
import usePermissions from "../hooks/usePermissions";
import dealsService from "../services/dealsService";
import { apiErrorText } from "../utils/apiError";
import brokerAccessService from "../services/brokerAccessService";
import { serializeDealForm } from "../utils/serializeDealForm";
import { toFormValues } from "../utils/toFormValues";
import { filterLookupOptions } from "../utils/lookupFilter";
import { withAgentPlaceholder } from "../utils/agentLookupHint";
import useSelectOptionFallback from "../hooks/useSelectOptionFallback";
import {
  agentRosterOptions,
  ownBrokerageId,
} from "../utils/brokerAgentRoster";
import {
  INTEREST_FIELD_RENDERERS,
  ShareBoundsBanner,
} from "./interestInput";

// Field-level gating is driven by `canEditField` (utils/permissions), which
// prefers the server's per-row `editable_for_me` and falls back to the local
// mirror of DEAL_FIELD_ROLE_GROUPS. It subsumes the assignment/status special
// cases this file used to carry and adds the handoff + operator-notification
// fields the server also gates.
//
// This drawer HIDES gated fields rather than showing them read-only (unlike
// the detail page's in-place form, which shows them as static values). That
// is a CREATE-mode judgement: there is no stored value to display, so a
// read-only row would render an empty "—" for a field the user can't set.
//
// The drawer's only live call site is mode="create" (dealsList). If the edit
// branch is ever revived, revisit this — on an existing deal, hiding a gated
// field hides real data, and the read-only treatment used by
// pages/dealDetail/index.jsx is the better fit.
//
// Hidden fields never enter form state, and the serializer is given the same
// predicate as a second line of defence.

// Default WigMetadataForm field predicate, replicated so our gating composes
// with — rather than replaces — the library's hide/editable filtering.
const defaultFieldVisible = (row) => !row.hide && row.editable !== false;

// Pull a human-readable message out of an axios error, preferring the
// FastAPI `detail` field (where the 409/422 reason lives).
const errText = (err, metadata) => apiErrorText(err, { metadata });

const DealFormDrawer = ({
  open,
  mode = "edit",
  deal = null,
  metadata = [],
  onClose,
  onSaved,
}) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const { canEditField } = usePermissions();
  const [form] = Form.useForm();
  // Popup host for every Select / DatePicker inside this drawer — see the
  // ConfigProvider in the render below.
  const popupHostRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const isCreate = mode === "create";

  // Reset form + error state whenever the drawer (re)opens or the target
  // deal changes. WigMetadataForm applies `initialValues` itself, but we
  // also reset the host form so a previous edit's values don't leak across
  // opens.
  useEffect(() => {
    if (open) {
      setErrorMsg(null);
      // NOTE: do NOT call form.resetFields() here. WigMetadataForm resets and
      // repopulates the fields from `initialValues` in its own effect; a reset
      // here runs AFTER that (child effects fire first) and wipes an edit form
      // back to empty (UAT #25). The Drawer's destroyOnClose already gives each
      // open a fresh form.
    }
  }, [open, deal]);

  // Edit mode: coerce the deal's Date/Timestamp fields to dayjs so the metadata
  // form's <DatePicker>s render (the library doesn't do this at runtime — see
  // toFormValues). Create starts empty, so no coercion needed.
  const initialValues = useMemo(
    () => (isCreate ? {} : toFormValues(deal || {}, metadata)),
    [isCreate, deal, metadata]
  );

  // Permission-aware field gating. Composes with WigMetadataForm's default
  // hide/editable predicate, then additionally drops the brokerage field
  // (brokerage_id) without `deal.assign`, the agent / Warwick-landman fields
  // without `deal.assignPeople`, and the status field (deal_status) without
  // `deal.editStatus`. Dropping rather than disabling
  // keeps serializeDealForm clean — an omitted field is never sent.
  const canWriteField = useCallback(
    (field, metaRow) => canEditField(field, metaRow),
    [canEditField]
  );
  const fieldFilter = useMemo(
    () => (row) => {
      if (!defaultFieldVisible(row)) return false;
      return canWriteField(row.field, row);
    },
    [canWriteField]
  );

  // In CREATE mode only, surface the natural key `deal_key` as an optional
  // field (UAT #32): the server auto-generates it from the selected brokerage
  // when left blank, but the land team can also type one (the fallback when no
  // brokerage is chosen). Server metadata keeps `deal_key` hidden (migration
  // 055) so the grid keeps its pinned column and the edit form doesn't expose
  // the identifier — so we override it locally, and use this same augmented
  // metadata for serialization so a typed key is actually sent.
  const formMetadata = useMemo(() => {
    if (!isCreate || !Array.isArray(metadata)) return metadata;
    const DEF = "Optional — leave blank to auto-generate from the brokerage.";
    let found = false;
    const mapped = metadata.map((m) => {
      if (m?.field !== "deal_key") return m;
      found = true;
      return { ...m, hide: false, editable: true, definition: DEF };
    });
    if (!found) {
      mapped.unshift({
        field: "deal_key",
        label: "Deal Key",
        data_type: "Text",
        category: "Identifier",
        ord: 0,
        hide: false,
        editable: true,
        definition: DEF,
      });
    }
    return mapped;
  }, [isCreate, metadata]);

  // Is the Brokerage field actually rendered in this form? It is gated behind
  // `deal.assign` (Landman-only), so a BD.BrokerAgent never sees it and can
  // never supply `brokerage_id` — the server derives their brokerage from
  // their `broker_agent_access` mapping instead (tenancy) and mints the key
  // from that. Used by the submit pre-flight below so the "pick a brokerage"
  // prompt is only raised at users who have a brokerage picker to use.
  const brokerageFieldShown = useMemo(() => {
    const row = (Array.isArray(formMetadata) ? formMetadata : []).find(
      (m) => m?.field === "brokerage_id"
    );
    if (!row) return false;
    return defaultFieldVisible(row) && canWriteField("brokerage_id", row);
  }, [formMetadata, canWriteField]);

  const getAuth = async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  };

  // Live brokerage selection, used to scope the `broker_agents` lookup.
  //
  // This MUST be watched (not just read inside the fetcher) because the
  // library's LookupSelect fetches exactly ONCE per fetcher identity —
  // `useEffect(() => runQuery(""), [runQuery])`, with `runQuery` memoized on
  // the fetcher — and never re-fetches when the dropdown is opened. With the
  // fetchers memoized on `[]`, the agent list was fetched at mount, when no
  // brokerage was selected yet, and stayed empty for the life of the drawer
  // even after a brokerage was picked (UAT: "agent dropdown is empty, even
  // after selecting the broker"). Watching the value and rebuilding the
  // fetchers map gives the Select a NEW fetcher identity, which re-runs that
  // mount effect and re-scopes the options.
  const brokerageId = Form.useWatch("brokerage_id", form);

  // Lookup fetchers keyed by the metadata's `distinct_values_lookup`.
  // brokers_xref -> /brokers (new source-of-truth), label = company_name.
  //
  // These do not depend on any other field, so they are memoized once: their
  // function identities must stay stable or every LookupSelect re-fetches
  // (and flickers) each time the brokerage changes.
  const staticLookupFetchers = useMemo(
    () => ({
      brokers_xref: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listBrokers({ token: tk, userOid });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows.map((r) => ({
              id: r.id,
              label: r.company_name || r.vendor_code || `Broker ${r.id}`,
            }))
          : [];
        // /brokers has no server-side text filter; apply the typeahead
        // query client-side so the dropdown narrows as the user types.
        return filterLookupOptions(opts, query);
      },
      // warwick_landman -> /landmen, label = full_name.
      warwick_landman: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listLandmen({ token: tk, userOid });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows.map((r) => ({
              id: r.id,
              label: r.full_name || `Landman ${r.id}`,
            }))
          : [];
        return filterLookupOptions(opts, query);
      },
      aois: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listAois({ token: tk, userOid });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows.map((r) => ({ id: r.id, label: r.name || `AOI ${r.id}` }))
          : [];
        return filterLookupOptions(opts, query);
      },
      // deal_status -> /deal_status. The stored value IS the status string
      // (no separate surrogate id), so id === label. Mapped defensively per
      // the backend contract (deal_status ?? value ?? row).
      deal_status: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listDealStatuses({
          token: tk,
          userOid,
        });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = Array.isArray(rows)
          ? rows
              .map((r) => r?.deal_status ?? r?.value ?? r)
              .filter((v) => v != null && v !== "")
              .map((v) => ({ id: v, label: String(v) }))
          : [];
        return filterLookupOptions(opts, query);
      },
    }),
    // getAuth closes over auth context; intentionally rebuilt per render is
    // fine — the Select only calls the fetcher on mount / keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // A BD.BrokerAgent has no Brokerage picker (`deal.assign` is Landman-only),
  // so `brokerageId` above is always undefined for them and the agent roster had
  // nothing to scope to — an empty Agent dropdown for exactly the users who
  // need it most. Their brokerage is not a form value: the server derives it
  // from their `broker_agent_access` mapping and stamps it on the deal. Resolve
  // that same mapping here, by email, and use it as the scope.
  const [ownBrokerage, setOwnBrokerage] = useState(null);
  const userEmail =
    user?.idTokenClaims?.preferred_username || user?.username || "";
  useEffect(() => {
    // Only for the no-picker case; anyone with the Brokerage field scopes the
    // roster by what they picked, and has no mapping of their own anyway.
    if (!open || brokerageFieldShown || !userEmail) return undefined;
    let alive = true;
    (async () => {
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await brokerAccessService.listBrokerAccess({
          token: tk,
          userOid,
        });
        const rows = resp?.data?.data || resp?.data || [];
        if (alive) setOwnBrokerage(ownBrokerageId(rows, userEmail));
      } catch {
        // Non-fatal: the roster stays empty and the user can still save — the
        // server stamps the brokerage regardless of what the dropdown showed.
        if (alive) setOwnBrokerage(null);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brokerageFieldShown, userEmail]);

  // The brokerage the agent roster is scoped to: what the land team picked, or
  // the broker agent's own firm.
  const scopedBrokerageId = brokerageFieldShown ? brokerageId : ownBrokerage;

  const lookupFetchers = useMemo(
    () => ({
      ...staticLookupFetchers,
      // broker_agents -> broker_agent_access, scoped to `scopedBrokerageId`.
      // List-only: no brokerage in scope => empty dropdown (add agents via the
      // Brokers > Agents panel). The stored deal value is the agent's NAME, so
      // id === label === name. `form` from Form.useForm() is stable.
      broker_agents: async (query) => {
        // Prefer the derived value (this fetcher's identity is keyed to it);
        // fall back to reading the form for safety.
        const selected =
          scopedBrokerageId ?? form.getFieldValue("brokerage_id");
        if (selected == null || selected === "") return [];
        const { token: tk, userOid } = await getAuth();
        const resp = await brokerAccessService.listBrokerAccess({
          token: tk,
          userOid,
        });
        const rows = resp?.data?.data || resp?.data || [];
        return filterLookupOptions(agentRosterOptions(rows, selected), query);
      },
    }),
    // Rebuilt whenever the scoped brokerage changes so the agent Select
    // re-fetches its options — see the note on `brokerageId` above. `form` is
    // stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staticLookupFetchers, scopedBrokerageId]
  );

  // Say WHY the agent roster is empty before a brokerage is picked — an
  // unexplained blank Select with AntD's bare "No data" panel reads as a
  // broken dropdown. Only the placeholder differs, so serialization keeps
  // using `formMetadata`.
  const formMetadataWithHints = useMemo(
    () =>
      withAgentPlaceholder(formMetadata, scopedBrokerageId, {
        canPickBrokerage: brokerageFieldShown,
      }),
    [formMetadata, scopedBrokerageId, brokerageFieldShown]
  );

  // Safety net for the OTHER dropdowns: any editable select the server left
  // without options (a vocabulary whose `pick_lists` rows are not seeded in
  // this database) gets one from pick_lists / its legacy endpoint rather than
  // rendering "No data". See hooks/useSelectOptionFallback.
  const { metadata: formMetadataFinal, lookupFetchers: lookupFetchersFinal } =
    useSelectOptionFallback(formMetadataWithHints, lookupFetchers, "deals");

  const handleSave = async () => {
    setErrorMsg(null);
    try {
      // Enforce metadata `required` rules (e.g. deal_key) and the field-level
      // bounds rules (Delivered NRI ≤ 1) before serializing.
      await form.validateFields();
    } catch (err) {
      // AntD highlights the offending fields inline; also surface them in the
      // drawer's Alert and scroll the first one into view, since the form is
      // long enough that the failing field can be off-screen.
      const fields = err?.errorFields || [];
      if (fields.length) {
        setErrorMsg(
          fields
            .flatMap((f) => f.errors || [])
            .filter(Boolean)
            .join(" ")
        );
        form.scrollToField(fields[0].name, { block: "center" });
      }
      return;
    }

    const values = form.getFieldsValue(true);

    // UAT #32: a new deal's `deal_key` is auto-generated server-side from its
    // brokerage when left blank. If neither a Deal Key nor a brokerage is
    // provided the backend can't mint one — guard with a friendly message
    // rather than firing a request that will 400. Only applies when the user
    // actually has the Brokerage picker: a scoped broker-agent has no such
    // field, and the server stamps their own brokerage on the deal before
    // minting the key, so a blank-key create from them is valid.
    if (
      isCreate &&
      !values.deal_key &&
      brokerageFieldShown &&
      (values.brokerage_id == null || values.brokerage_id === "")
    ) {
      setErrorMsg(
        "Select a brokerage (a Deal Key will be generated automatically) or enter a Deal Key."
      );
      return;
    }

    const body = serializeDealForm(values, formMetadata, canWriteField);

    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      if (isCreate) {
        const resp = await dealsService.createDeal({
          token: tk,
          userOid,
          body,
        });
        const created = resp?.data?.data ?? resp?.data ?? {};
        message.success("Deal created");
        onSaved?.({ mode: "create", deal: created, body });
      } else {
        const dealKey = deal?.deal_key;
        const resp = await dealsService.updateDeal({
          token: tk,
          userOid,
          dealKey,
          body,
        });
        // PUT returns the full updated row; fall back to the merged local
        // view so callers can detect a renamed deal_key.
        const updated =
          resp?.data?.data ?? resp?.data ?? { ...deal, ...body };
        message.success("Deal updated");
        onSaved?.({ mode: "edit", deal: updated, body });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = errText(err);
      setErrorMsg(
        status === 409
          ? `Duplicate deal key: ${msg}`
          : status === 422
            ? `Validation error: ${msg}`
            : msg
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={isCreate ? "New Deal" : `Edit Deal ${deal?.deal_key ?? ""}`}
      // Full-screen rather than the default right-hand rail: the deal form is
      // ~50 metadata fields across several category cards, and the narrow rail
      // made it a single very long scroll. The body caps its content width so
      // the fields don't stretch across an ultrawide monitor.
      width="100%"
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {isCreate ? "Create" : "Save"}
          </Button>
        </Space>
      }
    >
      {/* Dropdown popups default to `document.body`, which puts them outside
          the drawer's own scroll box: the menu is then positioned against the
          VIEWPORT, so a field low on the screen flips its list up over
          unrelated cards, and the list does not travel with its field when the
          body scrolls. Hosting popups in this wrapper — an ancestor of every
          field — anchors each menu to the form that owns it. Scoped to the
          drawer rather than set app-wide: the root ConfigProvider also serves
          grids and page-level menus that are better off on the body. */}
      <div ref={popupHostRef} style={{ position: "relative" }}>
        <ConfigProvider
          getPopupContainer={(trigger) =>
            popupHostRef.current || trigger?.parentElement || document.body
          }
        >
          {errorMsg && (
            <Alert
              type="error"
              showIcon
              message={isCreate ? "Failed to create deal" : "Failed to save deal"}
              description={errorMsg}
              style={{ marginBottom: 16 }}
            />
          )}
          <Form
            form={form}
            layout="vertical"
            // Changing the brokerage re-scopes the agent roster, so a name picked
            // under the previous brokerage is no longer valid — drop it rather
            // than submit an agent who doesn't work for the selected firm.
            // Programmatic setFieldsValue (how WigMetadataForm seeds edit mode)
            // does not fire this, so initial population is unaffected.
            onValuesChange={(changed) => {
              if (Object.prototype.hasOwnProperty.call(changed, "brokerage_id")) {
                form.setFieldValue("agent", undefined);
              }
            }}
            style={{ maxWidth: 1200, margin: "0 auto" }}
          >
            {/* WigMetadataForm owns the category Cards here (groupByCategory), so
                the banner sits at the top of the drawer body rather than inside the
                Terms card — one place, all failures listed. */}
            <ShareBoundsBanner />
            <WigMetadataForm
              metadata={formMetadataFinal}
              initialValues={initialValues}
              form={form}
              fieldFilter={fieldFilter}
              lookupFetchers={lookupFetchersFinal}
              customRenderers={INTEREST_FIELD_RENDERERS}
              groupByCategory
              // Two-up now that the drawer is full-screen — matches the deal
              // detail page's edit form.
              columns={2}
            />
          </Form>
        </ConfigProvider>
      </div>
    </Drawer>
  );
};

export default DealFormDrawer;
